import { useEffect } from "react";

//DEPENDENCIES
import { useNavigate, useRouteError, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";

//ASSETS
import NotFoundImage from "../../assets/images/not-found.svg?react";
import MaintenanceImage from "../../assets/images/maintenance.svg?react";

//COMPONENTS
import Button from "../../components/Button/Button";

//UTILS
import { openExternal } from "../../components/NavBar/utils/utils";

//STYLES
import styles from "./ErrorPage.module.scss";

const ONBOARDING_URL = "https://seleccion-cuenta.allaria.com.ar";

interface ErrorText {
  statusText?: string;
  message?: string;
}

interface ErrorConfig {
  image: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  buttonText: string;
  buttonAction: () => void;
  secondaryButtonText?: string;
  secondaryButtonAction?: () => void;
}

const ErrorPage = () => {
  const { errorPageContainer, errorPageButtons } = styles;
  const navigate = useNavigate();
  const location = useLocation();
  const error = useRouteError() as ErrorText;

  const errorToShow = error?.statusText || error?.message;

  const isRoleError = location.pathname === "/error-role";

  // Error configurations
  const errorConfigs: Record<string, ErrorConfig> = {
    "Not Found": {
      image: NotFoundImage,
      title: "No pudimos encontrar la página",
      description:
        "Es posible que la dirección sea inexistente o que ya no esté disponible",
      buttonText: "Ir al inicio",
      buttonAction: () => navigate("/"),
    },
    "Role Error": {
      image: MaintenanceImage,
      title: "Acceso limitado",
      description: "La plataforma no está disponible para tu usuario.",
      buttonText: "Volver al inicio",
      buttonAction: () => navigate("/login"),
      secondaryButtonText: "Crear cuenta",
      secondaryButtonAction: () => {
        void openExternal(ONBOARDING_URL);
      },
    },
    default: {
      image: MaintenanceImage,
      title: "Lo sentimos, algo ocurrió",
      description: "Intentá recargar el sitio.",
      buttonText: "Recargar",
      buttonAction: () => navigate("/"),
    },
  };

  const getConfig = () => {
    if (isRoleError) return errorConfigs["Role Error"];
    return errorConfigs[errorToShow || "default"] || errorConfigs.default;
  };

  const config = getConfig();
  const ImageComponent = config.image;

  useEffect(() => {
    const isNotFound =
      error?.statusText === "Not Found" || error?.message === "Not Found";

    if (error && !isRoleError && !isNotFound) {
      Sentry.captureException(error, {
        tags: { module: "ErrorPage" },
        extra: {
          pathname: location.pathname,
          statusText: error.statusText,
          message: error.message,
        },
      });
    }
  }, [error]);

  return (
    <div className={errorPageContainer}>
      <ImageComponent />
      <h3>{config.title}</h3>
      <p>{config.description}</p>
      <div className={errorPageButtons}>
        <Button
          variant="primary"
          text={config.buttonText}
          onPress={config.buttonAction}
        />
        {config.secondaryButtonText && config.secondaryButtonAction && (
          <Button
            variant="secondary"
            text={config.secondaryButtonText}
            onPress={config.secondaryButtonAction}
          />
        )}
      </div>
    </div>
  );
};

export default ErrorPage;
